import { createBrowserSignals } from "./browserSignals";
import { createController, isLoopback } from "./controller";
import { createRequestAdapter } from "./requests";
import type { LocalConfig } from "./contracts";
import { safely } from "./contracts";
import { SDK_VERSION, sdkCapabilities } from "./sdkCapabilities";

export const localMode =
  process.env.NODE_ENV === "development" &&
  isLoopback(safely(() => window.location.origin, ""));
const browser = createBrowserSignals();
export const injectedSeed = browser.injectedSeed;
export const noteVisibility = (visible: boolean) =>
  browser.signals.visibility?.(visible);
export const controller = createController({
  signals: browser.signals,
  mode: localMode ? "local" : "deployment",
  bootstrapDeadline: performance.now() + 60000,
  allowDeploymentPropagation:
    process.env.REACT_APP_OBSERVABILITY_PROPAGATION_ALLOWED === "true" &&
    sdkCapabilities(SDK_VERSION).deploymentPropagation,
  deploymentEndpoint: {
    exporterUrl: process.env.REACT_APP_OBSERVABILITY_EXPORTER_URL,
    allowedHost: process.env.REACT_APP_OBSERVABILITY_EXPORTER_HOST,
  },
  origin: safely(() => window.location.origin, ""),
  clock: { now: () => performance.now(), epochNow: () => Date.now() },
  schedule(ms, work) {
    const timer = setTimeout(work, ms);
    return () => clearTimeout(timer);
  },
  defer: (work) => {
    void Promise.resolve().then(() => safely(work, undefined));
  },
  lifecycle: {
    listen(onHidden, onVisible) {
      const visibility = () =>
        document.visibilityState === "hidden" ? onHidden() : onVisible();
      window.addEventListener("pagehide", onHidden);
      window.addEventListener("pageshow", onVisible);
      document.addEventListener("visibilitychange", visibility);
      if (document.visibilityState === "hidden") onHidden();
      return () => {
        window.removeEventListener("pagehide", onHidden);
        window.removeEventListener("pageshow", onVisible);
        document.removeEventListener("visibilitychange", visibility);
      };
    },
  },
});
export const { withTelemetry, physicalBaseQuery } = createRequestAdapter(
  controller,
  process.env.NODE_ENV === "development"
    ? (event) => require("./localDiagnostics").localDiagnostics.request(event)
    : undefined
);

/** Local/dev source only. No storage, remote flags, production console hook or defaults. */
export function installLocalControls() {
  if (
    process.env.NODE_ENV !== "development" ||
    !isLoopback(window.location.origin)
  )
    return;
  const target = window as Window & { wishlinkObservability?: unknown };
  target.wishlinkObservability = Object.freeze({
    configure: (config: LocalConfig) => controller.configure(config),
    disable: () => controller.disable(),
    exportHealth: () => controller.exportHealth(),
    diagnostics: Object.freeze({
      start: () =>
        require("./localDiagnostics").localDiagnostics.start(() =>
          controller.captureHome()
        ),
      stop: () => require("./localDiagnostics").localDiagnostics.stop(),
      snapshot: () => require("./localDiagnostics").localDiagnostics.snapshot(),
    }),
  });
}
